/** Shared palette — Atlassian-inspired accents on VPM forest brand. */

export type NavAccent = "blue" | "teal" | "purple" | "green" | "orange" | "red" | "indigo";

/** Saturated palette for bar/pie charts — distinct per series, readable on white. */
export const CHART_PALETTE = [
  "#1D63ED", // vibrant blue
  "#5E4DB2", // royal purple
  "#2BA7B4", // teal
  "#208454", // forest green
  "#E66F12", // orange
  "#C43022", // brick red
  "#8467D7", // lavender
  "#0EA5E9", // sky (extra departments)
] as const;

export const TASK_STATUS_CHART_COLORS: Record<string, string> = {
  not_started: "#8467D7",
  in_progress: "#E66F12",
  waiting: "#1D63ED",
  complete: "#208454",
  overdue: "#C43022",
};

export function chartColorForIndex(index: number): string {
  return CHART_PALETTE[index % CHART_PALETTE.length]!;
}

export function chartColorForTaskStatus(statusKey: string): string {
  const normalized = statusKey.toLowerCase().replace(/\s+/g, "_");
  return TASK_STATUS_CHART_COLORS[normalized] ?? chartColorForIndex(normalized.length);
}

/** Stable hash for assigning colors to names, departments, etc. */
export function hashString(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

export function colorForKey(key: string): string {
  return chartColorForIndex(hashString(key.trim().toLowerCase()));
}

export function avatarColorsForName(name: string): { background: string; color: string } {
  const color = colorForKey(name);
  return { background: `${color}22`, color };
}

export function departmentChipColors(name: string): { background: string; color: string; dot: string } {
  const color = colorForKey(name);
  return { background: `${color}18`, color, dot: color };
}

export const NAV_ACCENTS: Record<
  NavAccent,
  {
    icon: string;
    iconMuted: string;
    iconBg: string;
    activeBg: string;
    activeText: string;
    strip: string;
  }
> = {
  blue: {
    icon: "text-vpm-accent-blue",
    iconMuted: "text-vpm-accent-blue/70",
    iconBg: "bg-vpm-info-bg",
    activeBg: "bg-vpm-info-bg",
    activeText: "text-vpm-info-text",
    strip: "bg-vpm-accent-blue",
  },
  teal: {
    icon: "text-vpm-accent-teal",
    iconMuted: "text-vpm-accent-teal/70",
    iconBg: "bg-teal-50",
    activeBg: "bg-teal-50",
    activeText: "text-teal-800",
    strip: "bg-vpm-accent-teal",
  },
  purple: {
    icon: "text-vpm-accent-purple",
    iconMuted: "text-vpm-accent-purple/70",
    iconBg: "bg-vpm-discovery-bg",
    activeBg: "bg-vpm-discovery-bg",
    activeText: "text-vpm-discovery-text",
    strip: "bg-vpm-accent-purple",
  },
  green: {
    icon: "text-vpm-accent-green",
    iconMuted: "text-vpm-accent-green/70",
    iconBg: "bg-vpm-success-bg",
    activeBg: "bg-vpm-success-bg",
    activeText: "text-vpm-success-text",
    strip: "bg-vpm-accent-green",
  },
  orange: {
    icon: "text-vpm-accent-orange",
    iconMuted: "text-vpm-accent-orange/70",
    iconBg: "bg-orange-50",
    activeBg: "bg-orange-50",
    activeText: "text-orange-800",
    strip: "bg-vpm-accent-orange",
  },
  red: {
    icon: "text-vpm-accent-red",
    iconMuted: "text-vpm-accent-red/70",
    iconBg: "bg-vpm-danger-bg",
    activeBg: "bg-vpm-danger-bg",
    activeText: "text-vpm-danger-text",
    strip: "bg-vpm-accent-red",
  },
  indigo: {
    icon: "text-indigo-600",
    iconMuted: "text-indigo-600/70",
    iconBg: "bg-indigo-50",
    activeBg: "bg-indigo-50",
    activeText: "text-indigo-900",
    strip: "bg-indigo-500",
  },
};
